/**
 * React hook for the centralized real-time sync service
 *
 * Usage:
 * ```tsx
 * // In your App.tsx or root component:
 * function App() {
 *   useRealtimeSync();
 *   return <YourApp />;
 * }
 * ```
 */

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { initRealtimeSync, cleanupRealtimeSync, getSyncStatus } from '@/services/realtime-sync';

/**
 * Hook to initialize and manage the real-time sync service
 * Should be called once at the app root level
 */
export function useRealtimeSync(): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    // Initialize real-time sync
    const cleanup = initRealtimeSync(queryClient);

    // Log initialization in dev
    if (process.env.NODE_ENV === 'development') {
      const status = getSyncStatus();
      console.log(`[useRealtimeSync] Initialized - tracking ${status.trackedTables} tables`);
    }

    // Cleanup on unmount
    return () => {
      cleanup();
    };
  }, [queryClient]);
}

/**
 * Hook to get the current sync status
 * Useful for debugging or showing sync indicators
 */
export function useRealtimeSyncStatus() {
  return getSyncStatus();
}
